import { useEffect } from "react";
import type { FolderSummary } from "@/lib/store";
import { ChatTitleMenu } from "./ChatTitleMenu";

type Props = {
  title: string;
  hasActiveChat: boolean;
  currentFolderId: string | null;
  pinned: boolean;
  folders: FolderSummary[];
  sidebarOpen: boolean;
  backgroundBusy: boolean;
  artifactPanelOpen: boolean;
  ctxPercent: number | null;
  lastUsage: { promptTokens: number; contextLength: number | null } | null;
  activeContextLength: number | null;
  onShowSidebar: () => void;
  onNew: () => void;
  onRename: () => void;
  onSuggestTitle: () => void;
  onTogglePin: (pinned: boolean) => void;
  onMove: (folderId: string | null) => void;
  onDelete: () => void;
  onOpenArtifacts: () => void;
  onContinueInNew: () => void;
  continueBusy?: boolean;
  suggestBusy?: boolean;
};

export function ChatHeader(props: Props) {
  const {
    title,
    hasActiveChat,
    currentFolderId,
    pinned,
    folders,
    sidebarOpen,
    backgroundBusy,
    artifactPanelOpen,
    ctxPercent,
    lastUsage,
    activeContextLength,
    onShowSidebar,
    onNew,
    onRename,
    onSuggestTitle,
    onTogglePin,
    onMove,
    onDelete,
    onOpenArtifacts,
    onContinueInNew,
    continueBusy,
    suggestBusy,
  } = props;

  return (
    <header
      data-tauri-drag-region
      className={`fixed top-0 ${artifactPanelOpen ? "right-0 lg:right-[32rem]" : "right-0"} flex shrink-0 items-center justify-between border-b bg-background px-3 z-30`}
      style={{
        left: "var(--sb-w, 0px)",
        paddingTop: "calc(0.75rem + var(--titlebar-h, 0px))",
        paddingBottom: "0.75rem",
        minHeight: "calc(3.8rem + var(--titlebar-h, 0px))",
      }}
    >
      <div className="flex items-center gap-2">
        {!sidebarOpen && (
          <button
            onClick={onShowSidebar}
            className="relative flex size-8 items-center justify-center rounded-md bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
            title={backgroundBusy ? "Another chat is working…" : "Show sidebar"}
          >
            <i className="fa-solid fa-sidebar" />
            {backgroundBusy && (
              <span className="absolute -right-0.5 -top-0.5 inline-flex size-2 rounded-full bg-orange-500 ring-2 ring-background animate-pulse" />
            )}
          </button>
        )}
        <div className="flex flex-col min-w-0">
          <ChatTitleMenu
            title={title}
            hasActiveChat={hasActiveChat}
            currentFolderId={currentFolderId}
            pinned={pinned}
            folders={folders}
            onNew={onNew}
            onRename={onRename}
            onSuggestTitle={onSuggestTitle}
            onTogglePin={onTogglePin}
            onMove={onMove}
            onDelete={onDelete}
            onContinueInNew={onContinueInNew}
            continueBusy={continueBusy}
            suggestBusy={suggestBusy}
          />
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {ctxPercent != null && lastUsage && (
          <span
            className="hidden sm:inline-flex text-xs text-muted-foreground tabular-nums"
            title={`${lastUsage.promptTokens} / ${lastUsage.contextLength} tokens`}
          >
            {ctxPercent}% ctx
          </span>
        )}
        {ctxPercent == null && activeContextLength != null && (
          <span className="hidden sm:inline-flex text-xs text-muted-foreground tabular-nums" title="Context window">
            {(activeContextLength / 1024).toFixed(0)}k ctx
          </span>
        )}
        <button
          onClick={onOpenArtifacts}
          title="Artifacts in this chat"
          className="flex size-8 items-center justify-center rounded-md bg-muted/60 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <i className="fa-solid fa-file-code" />
        </button>
      </div>
    </header>
  );
}

type RenameDialogProps = {
  draft: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
};

export function RenameChatDialog({ draft, onChange, onSubmit, onCancel }: RenameDialogProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-lg border bg-background p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-sm font-semibold">Rename chat</h2>
        <input
          autoFocus
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
          className="mt-3 w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted">Cancel</button>
          <button onClick={onSubmit} className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90">Save</button>
        </div>
      </div>
    </div>
  );
}
